import { useState, useEffect, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import {
  RecipesByIngredientCategoriesQuery,
  useLazyRecipesByIngredientCategoriesQuery,
} from "@chef/state-management";
import { isNotEmptyArray } from "@chef/utils/array";

const RESULTS_PER_PAGE = 30;
const PAGE_STEP = 1;

export const useCategoryPagePagination = (initialData: {
  ingredientCategoryIds: number | number[] | undefined;
  paginatedRecipes: RecipesByIngredientCategoriesQuery["getRecipesByIngredientCategories"][];
  initialTotalPages: number;
}) => {
  const router = useRouter();
  const { ingredientCategoryIds, paginatedRecipes, initialTotalPages } =
    initialData;

  const [fetchedPages, setFetchedPages] = useState(paginatedRecipes);
  const [totalPages, setTotalPages] = useState(initialTotalPages);

  const [fetchRecipes, { isLoading, isFetching }] =
    useLazyRecipesByIngredientCategoriesQuery();
  const isLoadingRecipes = isLoading || isFetching;

  const pageNumber = parseInt(router.query["page"] as string) || PAGE_STEP;

  const recipes = useMemo(() => {
    return fetchedPages[pageNumber - 1] || [];
  }, [fetchedPages, pageNumber]);

  const handleFetchRecipes = useCallback(
    async (page: number) => {
      const { getRecipesByIngredientCategories } = await fetchRecipes({
        ingredientCategoryIds,
        pageNumber: page,
        resultsPerPage: RESULTS_PER_PAGE,
      }).unwrap();

      if (isNotEmptyArray(getRecipesByIngredientCategories)) {
        setFetchedPages((prevFetchedPages) => {
          const updatedFetchedPages = [...prevFetchedPages];
          updatedFetchedPages[page - 1] = getRecipesByIngredientCategories;
          return updatedFetchedPages;
        });

        setTotalPages((prevTotalPages) =>
          getRecipesByIngredientCategories.length === RESULTS_PER_PAGE
            ? Math.max(prevTotalPages, page + 1)
            : prevTotalPages,
        );
        return getRecipesByIngredientCategories;
      } else {
        return [];
      }
    },
    [fetchRecipes, ingredientCategoryIds],
  );

  useEffect(() => {
    if (!fetchedPages[pageNumber - 1]) {
      handleFetchRecipes(pageNumber).then((fetchedRecipes) => {
        if (!isNotEmptyArray(fetchedRecipes)) {
          router.replace(
            { query: { ...router.query, page: PAGE_STEP } },
            undefined,
            {
              shallow: true,
            },
          );
        }
      });
    } else if (pageNumber >= totalPages - 1) {
      handleFetchRecipes(totalPages + 1);
    }
  }, [pageNumber, fetchedPages, totalPages, handleFetchRecipes, router]);

  useEffect(() => {
    setFetchedPages(paginatedRecipes);
    setTotalPages(initialTotalPages);
  }, [ingredientCategoryIds, paginatedRecipes, initialTotalPages]);

  return {
    pageNumber,
    recipes,
    totalPages,
    isLoadingRecipes,
  };
};
